import { Molecule, wrap, today } from 'AppReact';
import styled from 'styled-components';
import { Icon } from 'atoms';
import { ClockSvg, StarSvg } from 'components/icons';
import { Category, CompletionTime, ParentCategory, Image, Initials } from './atoms';
import { ReversedProgressBar } from 'atoms';
import { MarginTopWrapper } from './wrappers';
import { FlexCenterWrapper, GenericWrapper, FlexCenterBetweenWrapper } from 'wrappers';
import { formatDistance, differenceInSeconds, differenceInDays } from 'date-fns';
import { getInitials } from 'utils/getInitials';

const template = styled.div`
  border: 1px solid #E3E3E3;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  margin-bottom: 10px;
  padding: 16px;
`;


export default Molecule({
  template,
  type: 'button',
  atoms: [
    {
      wrapper: FlexCenterBetweenWrapper,
      name: 'TopSection',
      atoms: [
        {
          wrapper: GenericWrapper,
          name: 'Category',
          atoms: [
            {
              component: ParentCategory,
              name: 'ParentCategory',
              displayValue: ({ assignment }) => assignment.parent_category ?? ''
            },
            {
              component: Category,
              name: 'Category',
              displayValue: ({ assignment }) => assignment?.category ?? ''
            }
          ]
        },
        {
          wrapper: FlexCenterWrapper,
          name: 'Images',
          atoms: [
            {
              component: Image,
              name: 'PropertyImage',
              renderIf: ({ assignment }) => assignment.property_icon,
              displayValue: ({ assignment }) => assignment.property_icon
            },
            {
              component: Initials,
              name: 'PropertyInitials',
              renderIf: ({ assignment }) => !assignment.property_icon,
              displayValue: ({ assignment }) => getInitials(assignment.property)
            },
            {
              component: Initials,
              name: 'TenantInitials',
              renderIf: ({ assignment }) => assignment.tenant,
              displayValue: ({ assignment }) => getInitials(assignment.tenant)
            },
            {
              component: Image,
              name: 'TechImage',
              renderIf: ({ image }) => image,
              displayValue: ({ image }) => image
            },
            {
              component: Initials,
              name: 'TechInitials',
              renderIf: ({ image }) => !image,
              displayValue: ({ name }) => getInitials(name)
            },
          ]
        }
      ]
    },
    {
      wrapper: wrap(FlexCenterBetweenWrapper, MarginTopWrapper),
      name: 'StatsWrapper',
      atoms: [
        {
          wrapper: FlexCenterWrapper,
          name: 'TimeWrapper',
          atoms: [
            {
              component: Icon,
              name: 'ClockIcon',
              renderIf: ({ assignment }) => assignment.status !== 'assigned',
              displayValue: ({ }) => ClockSvg({ width: '12', height: '12', color: window?.settings?.secondary_color ?? '#04333b' })
            },
            {
              component: CompletionTime,
              name: 'CompletionTime',
              renderIf: ({ assignment }) => assignment.status !== 'assigned',
              displayValue: ({ assignment }) => {
                let completionTime = '';
                if (assignment.status === 'completed' && assignment.event_at && assignment.assigned_at) {
                  completionTime = `Completed Within ${formatDistance(0, Math.abs(differenceInSeconds(new Date(assignment.event_at), assignment.assigned_at)) * 1000, { includeSeconds: true })}`;
                }
                if (assignment.status === 'callback') {
                  completionTime = `Re-opened ${formatDistance(0, differenceInSeconds(today(), assignment.updated_at) * 1000, { includeSeconds: true })} ago`;
                }
                if (assignment.status === 'withdrawn') {
                  completionTime = `Cancelled ${formatDistance(0, differenceInSeconds(today(), assignment.updated_at) * 1000, { includeSeconds: true })} ago`;
                }
                return completionTime;
              },
            }
          ]
        },
        {
          wrapper: FlexCenterWrapper,
          name: 'Rating',
          atoms: [
            {
              component: Icon,
              name: 'Star1',
              renderIf: ({ assignment }) => assignment.status === 'completed' && !!assignment.rating,
              displayValue: ({ assignment }) => StarSvg({ width: '12', height: '12', filled: true, color: assignment.rating >= 1 ? '#FFC700' : '#9BADB1' })
            },
            {
              component: Icon,
              name: 'Star2',
              renderIf: ({ assignment }) => assignment.status === 'completed' && !!assignment.rating,
              displayValue: ({ assignment }) => StarSvg({ width: '12', height: '12', filled: true, color: assignment.rating >= 2 ? '#FFC700' : '#9BADB1' })
            },
            {
              component: Icon,
              name: 'Star3',
              renderIf: ({ assignment }) => assignment.status === 'completed' && !!assignment.rating,
              displayValue: ({ assignment }) => StarSvg({ width: '12', height: '12', filled: true, color: assignment.rating >= 3 ? '#FFC700' : '#9BADB1' })
            },
            {
              component: Icon,
              name: 'Star4',
              renderIf: ({ assignment }) => assignment.status === 'completed' && !!assignment.rating,
              displayValue: ({ assignment }) => StarSvg({ width: '12', height: '12', filled: true, color: assignment.rating >= 4 ? '#FFC700' : '#9BADB1' })
            },
            {
              component: Icon,
              name: 'Star5',
              renderIf: ({ assignment }) => assignment.status === 'completed' && !!assignment.rating,
              displayValue: ({ assignment }) => StarSvg({ width: '12', height: '12', filled: true, color: assignment.rating >= 5 ? '#FFC700' : '#9BADB1' })
            },

          ]
        }
      ]
    },
    {
      wrapper: GenericWrapper,
      name: 'CompletionTimeOnHold',
      atoms: [
        {
          wrapper: FlexCenterBetweenWrapper,
          name: 'Time',
          atoms: [
            {
              component: CompletionTime,
              renderIf: ({ assignment }) => assignment.status === 'assigned',
              name: 'Submitted',
              displayValue: ({ assignment }) => `${formatDistance(0, differenceInSeconds(assignment.assigned_at, today()) * 1000, { includeSeconds: true })} ago`,
              displayState: ({ }) => ({ color: '#9EACB0' }),
            },
            {
              component: CompletionTime,
              renderIf: ({ assignment }) => assignment.status === 'assigned',
              name: 'Assigned',
              displayValue: ({ assignment }) => `${formatDistance(0, differenceInSeconds(assignment.assigned_at, today()) * 1000, { includeSeconds: true })} ago`,
              displayState: ({ color }) => ({ color, margin: '30px' }),
            },
          ]
        },
        {
          component: ReversedProgressBar,
          name: 'ProgressBar',
          renderIf: ({ assignment }) => assignment.status === 'assigned',
          displayValue: ({ }) => '',
          displayState: ({ assignment, color }) => ({
            color,
            reversed: true,
            progress: (differenceInDays(assignment.assigned_at, today()) / differenceInDays(assignment.assigned_at, today())) * 100
          })
        }
      ]
    },
  ],
})